// Editable knowledge bank state for the demo.
//
// The fixtures in mockData are the seed; anything the user adds or edits here
// lives in memory for the session and resets on reload, matching the rest of
// the demo's write actions. It is a module-level store rather than component
// state so an edit survives navigating to a call and back.
//
// Seed entries are never removable: transcripts cite them by id, and a citation
// that no longer resolves would break the call detail page.

import { useSyncExternalStore } from "react";
import {
  DEMO_KNOWLEDGE_ENTRIES,
  DEMO_LINE_CONTEXT,
  INBOUND_DEMO_TIMEZONE,
} from "./mockData";
import { getDayKey } from "./selectors";
import { KnowledgeEntry } from "./types";

export interface KnowledgeBankSnapshot {
  entries: KnowledgeEntry[];
  orgSummary: string;
  /** Keyed by practice id. */
  practiceSummaries: Record<string, string>;
}

/** Which tier an edit or new entry belongs to. */
export type KnowledgeScope =
  | { tier: "ORG" }
  | { tier: "PRACTICE"; practiceId: string };

export interface KnowledgeEntryDraft {
  scope: KnowledgeScope;
  question: string;
  answer: string;
}

const SEED_ENTRY_IDS = new Set(DEMO_KNOWLEDGE_ENTRIES.map((e) => e.id));

/** Seed entries are cited by the transcripts, so the UI keeps them read-only. */
export function isSeedEntry(entryId: string): boolean {
  return SEED_ENTRY_IDS.has(entryId);
}

function initialSnapshot(): KnowledgeBankSnapshot {
  return {
    entries: DEMO_KNOWLEDGE_ENTRIES,
    orgSummary: DEMO_LINE_CONTEXT.orgSummary,
    practiceSummaries: Object.fromEntries(
      DEMO_LINE_CONTEXT.perPracticeSummaries.map((s) => [
        s.practiceId,
        s.summary,
      ])
    ),
  };
}

let snapshot: KnowledgeBankSnapshot = initialSnapshot();
const listeners = new Set<() => void>();

function setSnapshot(next: KnowledgeBankSnapshot): void {
  snapshot = next;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getKnowledgeBank(): KnowledgeBankSnapshot {
  return snapshot;
}

export function useKnowledgeBank(): KnowledgeBankSnapshot {
  return useSyncExternalStore(subscribe, getKnowledgeBank);
}

/** Entries owned by one tier: the org tier, or a single practice's overlay. */
export function entriesForScope(
  bank: KnowledgeBankSnapshot,
  scope: KnowledgeScope
): KnowledgeEntry[] {
  return bank.entries.filter((entry) =>
    entry.tier === "ORG"
      ? scope.tier === "ORG"
      : scope.tier === "PRACTICE" && entry.practiceId === scope.practiceId
  );
}

export function summaryForScope(
  bank: KnowledgeBankSnapshot,
  scope: KnowledgeScope
): string {
  return scope.tier === "ORG"
    ? bank.orgSummary
    : bank.practiceSummaries[scope.practiceId] ?? "";
}

/** Keeps generated ids in the same shape as the seeds, e.g. "org-kb-010". */
function nextEntryId(scope: KnowledgeScope): string {
  const prefix =
    scope.tier === "ORG"
      ? "org-kb"
      : `prac-${scope.practiceId.replace(/^practice-/, "")}-kb`;
  const highest = snapshot.entries.reduce((max, entry) => {
    if (!entry.id.startsWith(`${prefix}-`)) {
      return max;
    }
    const suffix = Number(entry.id.slice(prefix.length + 1));
    return Number.isNaN(suffix) ? max : Math.max(max, suffix);
  }, 0);
  return `${prefix}-${String(highest + 1).padStart(3, "0")}`;
}

/**
 * Adds an entry. Staff writing an answer here makes it immediately
 * citable on future calls — historical transcripts are unaffected.
 */
export function addKnowledgeEntry(draft: KnowledgeEntryDraft): KnowledgeEntry {
  const base = {
    id: nextEntryId(draft.scope),
    question: draft.question.trim(),
    answer: draft.answer.trim(),
    lastReviewedAt: getDayKey(new Date().toISOString(), INBOUND_DEMO_TIMEZONE),
  };
  const entry: KnowledgeEntry =
    draft.scope.tier === "ORG"
      ? { ...base, tier: "ORG" }
      : { ...base, tier: "PRACTICE", practiceId: draft.scope.practiceId };
  setSnapshot({ ...snapshot, entries: [...snapshot.entries, entry] });
  return entry;
}

export function removeKnowledgeEntry(entryId: string): void {
  if (isSeedEntry(entryId)) {
    return;
  }
  setSnapshot({
    ...snapshot,
    entries: snapshot.entries.filter((entry) => entry.id !== entryId),
  });
}

export function setScopeSummary(scope: KnowledgeScope, summary: string): void {
  if (scope.tier === "ORG") {
    setSnapshot({ ...snapshot, orgSummary: summary });
    return;
  }
  setSnapshot({
    ...snapshot,
    practiceSummaries: {
      ...snapshot.practiceSummaries,
      [scope.practiceId]: summary,
    },
  });
}

/** True once anything has been added or edited, so the UI can offer a reset. */
export function hasUnsavedDemoEdits(bank: KnowledgeBankSnapshot): boolean {
  const seed = initialSnapshot();
  return (
    bank.entries.length !== seed.entries.length ||
    bank.orgSummary !== seed.orgSummary ||
    Object.entries(seed.practiceSummaries).some(
      ([practiceId, summary]) => bank.practiceSummaries[practiceId] !== summary
    )
  );
}

export function resetKnowledgeBank(): void {
  setSnapshot(initialSnapshot());
}
